/**
 * Discount Preview Renderer
 *
 * Scannt <price-list data-variant-id="..."> Elemente und overlayed sie
 * mit Code-rabattierten Preisen aus window.discountPreview.
 *
 * Hooks ins DOM:
 *   - <price-list>         data-variant-id, data-base-price, data-base-compare-at
 *   - <sale-price>         (innen)  -> Text wird auf finalCents gesetzt
 *   - <compare-at-price>   (innen)  -> Text auf originalCents, hidden entfernt
 *   - <on-sale-badge>      (auf Card / global)  -> hidden entfernt, Text mit Savings
 *
 * Re-rendert auf:
 *   - discount:updated Event von der Engine
 *   - variant:change   Event (PDP-Variant-Picker)
 *   - cart:change      Event (nach AddToCart)
 *   - MutationObserver für dynamisch nachgeladene Cards
 */

(function () {
  'use strict';

  function whenReady(cb) {
    var attempts = 0;
    var MAX = 100;
    (function poll() {
      if (window.discountPreview) { cb(); return; }
      if (++attempts >= MAX) {
        console.warn('[discount-renderer] window.discountPreview never appeared after', MAX * 50, 'ms; giving up.');
        return;
      }
      setTimeout(poll, 50);
    })();
  }

  const themeVars = window.themeVariables || {};
  const moneyFormat = (themeVars.settings && themeVars.settings.moneyFormat) || '{{amount}} €';
  const moneyWithCurrencyFormat = (themeVars.settings && themeVars.settings.moneyWithCurrencyFormat) || moneyFormat;
  const currencyCodeEnabled = Boolean(themeVars.settings && themeVars.settings.currencyCodeEnabled);
  const discountMode = (themeVars.settings && themeVars.settings.discountMode) || 'percentage';
  const discountBadgeTemplate = (themeVars.strings && themeVars.strings.discountBadge) || '-@@';

  function formatMoney(cents) {
    const fmt = currencyCodeEnabled ? moneyWithCurrencyFormat : moneyFormat;
    return formatWithFormat(cents, fmt);
  }

  // cents MUSS ein Integer (oder ein Integer als String) sein. data-base-price /
  // data-base-compare-at kommen aus Liquid `variant.price`, das bereits in Cents
  // ohne Dezimaltrenner ausgegeben wird. String-Eingaben werden defensiv geparsed.
  function formatWithFormat(cents, fmtString) {
    if (typeof cents === 'string') {
      cents = Number(cents.replace('.', ''));
    }
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const match = fmtString.match(placeholderRegex);

    function delimit(num, precision, thousands, decimal) {
      if (isNaN(num) || num == null) return '0';
      num = (num / 100).toFixed(precision);
      const parts = num.split('.');
      const major = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const minor = parts[1] ? decimal + parts[1] : '';
      return major + minor;
    }

    let value;
    switch (match ? match[1] : 'amount') {
      case 'amount':
        value = delimit(cents, 2, ',', '.'); break;
      case 'amount_no_decimals':
        value = delimit(cents, 0, ',', ''); break;
      case 'amount_with_space_separator':
        value = delimit(cents, 2, ' ', '.'); break;
      case 'amount_with_comma_separator':
        value = delimit(cents, 2, '.', ','); break;
      case 'amount_with_apostrophe_separator':
        value = delimit(cents, 2, "'", '.'); break;
      case 'amount_no_decimals_with_comma_separator':
        value = delimit(cents, 0, '.', ''); break;
      case 'amount_no_decimals_with_space_separator':
        value = delimit(cents, 0, ' ', ''); break;
      case 'amount_no_decimals_with_apostrophe_separator':
        value = delimit(cents, 0, "'", ''); break;
      default:
        value = delimit(cents, 2, ',', '.'); break;
    }
    return fmtString.replace(placeholderRegex, value);
  }

  function formatSavings(info) {
    const value = discountMode === 'percentage'
      ? info.percentageOff + '%'
      : formatMoney(info.savingsCents);
    // js-variables.liquid injiziert savings: '@@' in den übersetzten Badge-String.
    // Sollte der Platzhalter aus der Locale verschwinden (defekte Übersetzung),
    // hängen wir den Wert defensiv an statt einen Badge ohne Prozent zu rendern.
    if (discountBadgeTemplate.indexOf('@@') === -1) {
      return (discountBadgeTemplate + ' ' + value).trim();
    }
    return discountBadgeTemplate.split('@@').join(value);
  }

  /**
   * Wendet eine PriceInfo auf ein <price-list>-Element an.
   */
  function applyToPriceList(priceListEl, priceInfo) {
    if (!priceListEl || !priceInfo) return;

    // Cart-Line-Items werden bereits server-seitig via item_order_discount korrekt
    // gerendert — die überspringen wir, sonst doppelte Subtraktion.
    if (priceListEl.dataset.context === 'line-item') return;

    const basePrice = Number(priceListEl.dataset.basePrice || 0);
    const baseCompareAt = Number(priceListEl.dataset.baseCompareAt || 0);

    const hasCodeDiscount = priceInfo.savingsCents > 0 && priceInfo.finalCents < basePrice;
    const hasIntrinsicSale = baseCompareAt > basePrice;

    const salePrice = priceListEl.querySelector('sale-price');
    const compareAtPrice = priceListEl.querySelector('compare-at-price');

    if (!salePrice) return;

    if (hasCodeDiscount) {
      const displayCompareAt = hasIntrinsicSale ? baseCompareAt : basePrice;

      salePrice.textContent = formatMoney(priceInfo.finalCents);
      salePrice.classList.add('text-on-sale');
      salePrice.classList.remove('text-subdued');
      salePrice.dataset.discountApplied = '1';

      let cmpEl = compareAtPrice;
      if (!cmpEl) {
        cmpEl = document.createElement('compare-at-price');
        cmpEl.className = 'text-subdued line-through';
        cmpEl.dataset.dpInjected = '1';
        salePrice.parentNode.insertBefore(cmpEl, salePrice.nextSibling);
      }
      cmpEl.textContent = formatMoney(displayCompareAt);
      cmpEl.removeAttribute('hidden');
      cmpEl.classList.add('line-through');
    } else if (salePrice.dataset.discountApplied === '1') {
      salePrice.textContent = formatMoney(basePrice);
      delete salePrice.dataset.discountApplied;
      if (compareAtPrice && compareAtPrice.dataset.dpInjected === '1') {
        compareAtPrice.remove();
      } else if (!hasIntrinsicSale) {
        salePrice.classList.remove('text-on-sale');
        if (compareAtPrice) {
          compareAtPrice.setAttribute('hidden', '');
        }
      } else if (compareAtPrice) {
        compareAtPrice.textContent = formatMoney(baseCompareAt);
        compareAtPrice.removeAttribute('hidden');
      }
    }
  }

  function applyToBadge(badgeEl, priceInfo, basePrice, baseCompareAt) {
    if (!badgeEl || !priceInfo) return;

    const hasCodeDiscount = priceInfo.savingsCents > 0 && priceInfo.finalCents < basePrice;
    const hasIntrinsicSale = baseCompareAt > basePrice;

    if (hasCodeDiscount) {
      const refPrice = hasIntrinsicSale ? baseCompareAt : basePrice;
      const totalSavings = refPrice - priceInfo.finalCents;
      const totalPercentage = refPrice > 0 ? Math.round((totalSavings * 100) / refPrice) : 0;

      badgeEl.textContent = formatSavings({ savingsCents: totalSavings, percentageOff: totalPercentage });
      badgeEl.removeAttribute('hidden');
      badgeEl.dataset.discountApplied = '1';
    } else if (badgeEl.dataset.discountApplied === '1') {
      if (hasIntrinsicSale) {
        const savings = baseCompareAt - basePrice;
        const pct = Math.round((savings * 100) / baseCompareAt);
        badgeEl.textContent = formatSavings({ savingsCents: savings, percentageOff: pct });
      } else {
        badgeEl.setAttribute('hidden', '');
      }
      delete badgeEl.dataset.discountApplied;
    }
  }

  function findPriceListForVariant(variantId) {
    if (!variantId) return null;
    const numericId = parseInt(variantId, 10);
    if (!Number.isFinite(numericId) || numericId <= 0) return null;
    const lists = document.querySelectorAll(
      `price-list[data-variant-id="${numericId}"]:not([data-context="line-item"])`
    );
    return lists[0] || null;
  }

  /**
   * Synct price-list / on-sale-badge data-Attribute auf die neu gewählte Variante.
   *
   * Shopifys <sale-price form>/<compare-at-price form> Custom-Elemente updaten
   * bei variant:change NUR ihren eigenen Text. Der <price-list>-Wrapper behält
   * data-variant-id / data-base-price / data-base-compare-at der initial
   * gerenderten Variante (statisches Liquid). Die Discount-Preview liest aber
   * genau diese Attribute, ohne Sync würde jeder Variantenwechsel den Rabatt
   * auf den Preis der ERSTEN Variante rechnen und den (von Shopify korrekt
   * gesetzten) Preis wieder mit erstePreis * (1 - prozent) überschreiben.
   * Der Preis bleibt dann unabhängig von der Auswahl eingefroren.
   *
   * Quelle der Wahrheit ist die Variante aus dem variant:change-Event. Scope:
   * primär die ans Formular gebundenen Elemente (sale-price[form]/badge[form]),
   * Fallback per data-product-id.
   */
  function syncVariantDataset(event) {
    const detail = event && event.detail;
    const variant = detail && detail.variant;
    if (!variant || !variant.id) return;

    const elements = new Set();
    const form = event.target;
    // ACHTUNG: form.id ist auf Produkt-Formularen durch <input name="id">
    // (die Varianten-ID) verschattet und liefert das Input-Element statt der
    // String-ID. Darum hart über getAttribute('id') lesen.
    const formId = form && form.getAttribute ? form.getAttribute('id') : null;

    if (formId) {
      const sel = (window.CSS && CSS.escape) ? CSS.escape(formId) : formId;
      document.querySelectorAll(`sale-price[form="${sel}"]`).forEach((sp) => {
        const pl = sp.closest('price-list[data-variant-id]:not([data-context="line-item"])');
        if (pl) elements.add(pl);
      });
      document.querySelectorAll(`on-sale-badge[form="${sel}"][data-variant-id]`).forEach((b) => elements.add(b));
    }

    // Fallback: keine form-gebundenen Elemente gefunden -> per product-id matchen.
    if (!elements.size && detail.product && detail.product.id) {
      const pid = String(detail.product.id);
      document.querySelectorAll(`price-list[data-product-id="${pid}"]:not([data-context="line-item"])`).forEach((pl) => elements.add(pl));
      document.querySelectorAll(`on-sale-badge[data-product-id="${pid}"]`).forEach((b) => elements.add(b));
    }

    elements.forEach((el) => {
      el.dataset.variantId = String(variant.id);
      if (el.tagName === 'PRICE-LIST') {
        el.dataset.basePrice = String(variant.price);
        // compare_at_price ist null wenn kein Vergleichspreis. Attribut dann
        // entfernen, damit es mit dem Liquid-Render (nur bei vorhandenem
        // compare_at gesetzt) konsistent bleibt.
        if (variant.compare_at_price) {
          el.dataset.baseCompareAt = String(variant.compare_at_price);
        } else {
          delete el.dataset.baseCompareAt;
        }
      }
    });
  }

  function collectTargets(root) {
    const scope = root || document;
    const lists = Array.from(scope.querySelectorAll('price-list[data-variant-id]'));
    const badges = Array.from(scope.querySelectorAll('on-sale-badge[data-variant-id]'));
    const upsellPrices = Array.from(scope.querySelectorAll('.upsell-item__price[data-variant-id]'));

    const variantIds = new Set();
    lists.forEach((el) => variantIds.add(el.dataset.variantId));
    badges.forEach((el) => variantIds.add(el.dataset.variantId));
    upsellPrices.forEach((el) => variantIds.add(el.dataset.variantId));

    return {
      lists: lists,
      badges: badges,
      upsellPrices: upsellPrices,
      variantIds: Array.from(variantIds)
    };
  }

  function refresh(root) {
    if (!window.discountPreview || !window.discountPreview.enabled) {
      return;
    }

    // hasActiveDiscounts deckt code- UND automatic-discounts ab. Fallback auf
    // getActiveCodes für den (theoretischen) Fall einer aelteren Engine ohne
    // das neue API.
    const hasDiscounts = typeof window.discountPreview.hasActiveDiscounts === 'function'
      ? window.discountPreview.hasActiveDiscounts()
      : window.discountPreview.getActiveCodes().length > 0;
    if (!hasDiscounts) {
      resetAll(root);
      return;
    }

    const targets = collectTargets(root);
    if (!targets.variantIds.length) return;

    const previews = window.discountPreview.getPreview(targets.variantIds);

    targets.lists.forEach((el) => {
      const info = previews[el.dataset.variantId];
      if (info) applyToPriceList(el, info);
    });

    targets.badges.forEach((el) => {
      const info = previews[el.dataset.variantId];
      const pl = findPriceListForVariant(el.dataset.variantId);
      const basePrice = pl ? Number(pl.dataset.basePrice || 0) : 0;
      const baseCompareAt = pl ? Number(pl.dataset.baseCompareAt || 0) : 0;
      if (info) applyToBadge(el, info, basePrice, baseCompareAt);
    });

    // Upsell-Items: vom upsell-manager.js gehandhabt (Phase 6).
  }

  function resetAll(root) {
    const targets = collectTargets(root);
    targets.lists.forEach((el) => {
      const sale = el.querySelector('sale-price[data-discount-applied="1"]');
      if (!sale) return;
      const basePrice = Number(el.dataset.basePrice || 0);
      const baseCompareAt = Number(el.dataset.baseCompareAt || 0);
      sale.textContent = formatMoney(basePrice);
      delete sale.dataset.discountApplied;
      const compareAt = el.querySelector('compare-at-price');
      if (compareAt && compareAt.dataset.dpInjected === '1') {
        // Injizierte Streichpreise komplett entfernen, kein dauerhafter DOM-Rest.
        compareAt.remove();
      } else if (compareAt) {
        if (baseCompareAt > basePrice) {
          compareAt.textContent = formatMoney(baseCompareAt);
          compareAt.removeAttribute('hidden');
        } else {
          compareAt.setAttribute('hidden', '');
        }
      }
      if (baseCompareAt <= basePrice) {
        sale.classList.remove('text-on-sale');
      }
    });
    targets.badges.forEach((el) => {
      if (el.dataset.discountApplied !== '1') return;
      const pl = findPriceListForVariant(el.dataset.variantId);
      const basePrice = pl ? Number(pl.dataset.basePrice || 0) : 0;
      const baseCompareAt = pl ? Number(pl.dataset.baseCompareAt || 0) : 0;
      if (baseCompareAt > basePrice) {
        const savings = baseCompareAt - basePrice;
        const pct = Math.round((savings * 100) / baseCompareAt);
        el.textContent = formatSavings({ savingsCents: savings, percentageOff: pct });
      } else {
        el.setAttribute('hidden', '');
      }
      delete el.dataset.discountApplied;
    });
  }

  // ─────────────────────────────────────────────────────────────
  // Toast-Notification für aktive Rabattcodes
  // - Pro Code 1 Toast, oben rechts, slide-in, Progress-Bar 7s
  // - Anzeige nur EINMAL pro Code-Aktivierungszyklus (localStorage). Wenn
  //   der Code wieder entfernt wird (Chip-X oder Cart-Removal), wird das
  //   Flag in updateToasts() gelöscht, damit ein erneutes Hinzufuegen den
  //   Toast wieder triggern kann (gewollte UX).
  // - Auto-Dismiss + X-Button (X = nur Hide-Toast, Code bleibt aktiv)
  // ─────────────────────────────────────────────────────────────
  // Single source of truth für Anzeigedauer: CSS-Custom-Property --dp-toast-duration
  // (gesetzt auf :root in theme.css) wird sowohl von der CSS-Progress-Bar-Animation als
  // auch hier vom Auto-Dismiss-Timer gelesen. Änderung an einer Stelle wirkt synchron.
  // Browser normalisieren CSS-Time-Values teilweise zu Sekunden ("7s" statt "7000ms"),
  // daher beide Einheiten korrekt parsen.
  function readToastDurationMs() {
    var raw = String(getComputedStyle(document.documentElement).getPropertyValue('--dp-toast-duration')).trim();
    var num = parseFloat(raw);
    if (!Number.isFinite(num) || num <= 0) return 7000;
    // Endet auf 'ms' → schon Millisekunden. Endet nur auf 's' → Sekunden, mal 1000.
    if (/ms$/i.test(raw)) return num;
    if (/s$/i.test(raw)) return num * 1000;
    return num;
  }
  const TOAST_STORAGE_PREFIX = 'zh_dp_toast_last_';
  // Codes die in dieser Tab-Session schon einen toast getriggert haben
  // (oder bei denen wir die isToastDue-Prüfung schon gemacht haben).
  // Wird beim Removal aus state.codes geleert, damit re-apply wieder toastet.
  const shownCodes = new Set();
  const activeToastNodes = new Map(); // code -> Toast-DOM-Node

  function getRegion() {
    return document.querySelector('discount-active-banner[data-discount-toast-region]');
  }

  function getStack() {
    const region = getRegion();
    return region ? region.querySelector('[data-discount-toast-stack]') : null;
  }

  function getTemplate() {
    const region = getRegion();
    return region ? region.querySelector('[data-discount-toast-template]') : null;
  }

  function isToastDue(code) {
    try {
      // Toast pro Code-Aktivierungszyklus nur einmal zeigen. Das Flag wird
      // in updateToasts() gelöscht sobald der Code nicht mehr aktiv ist,
      // damit erneutes Hinzufuegen wieder toastet.
      return !localStorage.getItem(TOAST_STORAGE_PREFIX + code);
    } catch (_) {
      return true;
    }
  }

  function markToastShown(code) {
    try {
      localStorage.setItem(TOAST_STORAGE_PREFIX + code, String(Date.now()));
    } catch (_) { /* ignore quota / private mode */ }
  }

  function dismissToast(code, animate) {
    const node = activeToastNodes.get(code);
    if (!node) return;
    activeToastNodes.delete(code);
    if (!animate) {
      node.remove();
      return;
    }
    node.classList.add('dp-toast--leaving');
    const onEnd = () => {
      node.removeEventListener('animationend', onEnd);
      node.remove();
    };
    node.addEventListener('animationend', onEnd);
    // Fallback falls animationend nicht feuert
    setTimeout(() => { if (node.isConnected) node.remove(); }, 400);
  }

  function spawnToast(code) {
    const stack = getStack();
    const tpl = getTemplate();
    if (!stack || !tpl) return;
    if (activeToastNodes.has(code)) return;

    const frag = tpl.content.cloneNode(true);
    const node = frag.querySelector('[data-discount-toast]');
    if (!node) return;
    const codeEl = node.querySelector('[data-discount-toast-code]');
    if (codeEl) codeEl.textContent = code;

    // Neuestes oben einfuegen
    stack.insertBefore(node, stack.firstChild);
    activeToastNodes.set(code, node);

    const dismissBtn = node.querySelector('[data-discount-toast-dismiss]');
    if (dismissBtn) {
      dismissBtn.addEventListener('click', () => dismissToast(code, true));
    }

    // Auto-Dismiss
    setTimeout(() => dismissToast(code, true), readToastDurationMs());

    markToastShown(code);
  }

  function updateToasts() {
    const dp = window.discountPreview;
    const codes = dp ? dp.getActiveCodes() : [];
    const pending = dp && typeof dp.getPendingCodes === 'function' ? dp.getPendingCodes() : [];
    const pendingSet = new Set(pending.map((c) => c.toLowerCase()));

    // Pending codes (auf leerem cart vorgemerkt) NICHT als toast zeigen. Erst
    // wenn der code applicable wird (cart hat items), spawned der toast, das
    // matched die UX-Erwartung "Rabattcode aktiv" mit echter Aktivierung.
    const applicableCodes = codes.filter((c) => !pendingSet.has(c.toLowerCase()));
    const applicableSet = new Set(applicableCodes);
    const stateCodeSet = new Set(codes);

    // Toasts wegnehmen wenn code nicht mehr applicable (pending oder removed).
    activeToastNodes.forEach((_, code) => {
      if (!applicableSet.has(code)) {
        dismissToast(code, true);
      }
    });

    // Flag-Cleanup nur wenn der Code WIRKLICH aus state.codes entfernt wurde
    // (Chip-X, Cart-Auto-Cleanup). Pending-Status (Cart geleert) zählt NICHT
    // als "entfernt": sonst würde nach Refill der Toast erneut spawnen
    // obwohl der User den Code nie aktiv removed hat.
    shownCodes.forEach((code) => {
      if (!stateCodeSet.has(code)) {
        try { localStorage.removeItem(TOAST_STORAGE_PREFIX + code); } catch (_) {}
        shownCodes.delete(code);
      }
    });

    // Erstmalig applicable in dieser Session: Toast prüfen und ggf. spawnen.
    applicableCodes.forEach((code) => {
      if (shownCodes.has(code)) return;
      shownCodes.add(code);
      if (isToastDue(code)) {
        spawnToast(code);
      }
    });
  }

  function init() {
    updateToasts();
    refresh();

    document.documentElement.addEventListener('discount:updated', () => {
      updateToasts();
      refresh();
    });

    // Synchron im selben Tick laufen lassen: Shopifys sale-price Custom-Element
    // hat sich frueher (in connectedCallback) registriert, dadurch läuft sein
    // Handler zuerst (Form-Level-Listener feuern beim Bubbling vor diesem
    // documentElement-Listener). Wir synchronisieren erst die price-list-
    // data-Attribute auf die neue Variante und rechnen DANN den Rabatt, sonst
    // bliebe der Preis auf der ersten Variante eingefroren.
    document.documentElement.addEventListener('variant:change', (event) => {
      syncVariantDataset(event);
      refresh();
    });

    // Belt-and-suspenders: nach dem Frame nochmal anwenden, falls Shopify
    // intern noch eine RAF-Update macht.
    document.documentElement.addEventListener('variant:change', () => {
      // Wrap in an arrow: requestAnimationFrame would otherwise call refresh
      // with a DOMHighResTimeStamp, so collectTargets received a number as
      // root and threw "scope.querySelectorAll is not a function".
      requestAnimationFrame(() => refresh());
    });

    document.documentElement.addEventListener('cart:change', () => {
      refresh();
    });

    // Observer auf document.body feuert pro DOM-Mutation. refresh() wird darum
    // über requestAnimationFrame gebuendelt, damit Burst-Mutations (Drawer-Open,
    // Lazy-Sections, Video-Players) nur einen refresh() pro Frame ausloesen.
    let refreshScheduled = false;
    function scheduleRefresh() {
      if (refreshScheduled) return;
      refreshScheduled = true;
      requestAnimationFrame(() => {
        refreshScheduled = false;
        refresh();
      });
    }
    const observer = new MutationObserver((mutations) => {
      for (let i = 0; i < mutations.length; i++) {
        const added = mutations[i].addedNodes;
        for (let j = 0; j < added.length; j++) {
          const node = added[j];
          if (node.nodeType !== 1) continue;
          if (node.matches && (node.matches('price-list[data-variant-id]') || node.querySelector('price-list[data-variant-id]'))) {
            scheduleRefresh();
            return;
          }
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  whenReady(function () {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
  });

  window.discountPreviewRenderer = {
    refresh: refresh,
    // Internal pure formatters exposed for unit tests only.
    _internal: {
      formatMoney: formatMoney,
      formatWithFormat: formatWithFormat,
      formatSavings: formatSavings
    }
  };
})();
